import { Component } from '@theme/component'
import { onAnimationEnd } from '@theme/utilities'
import { StandardEvents } from '@shopify/events'

/**
 * @returns {boolean}
 */
function preferencesProcessingAllowed() {
  try {
    return (
      window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() ?? false
    )
  } catch {
    return false
  }
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount']

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10)
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : ''
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate)
    this.ensureCartBubbleIsCorrect()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    )
  }

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const totalQuantity = cart?.totalQuantity

        if (typeof totalQuantity === 'number') {
          this.renderCartBubble(totalQuantity, false)
          return
        }

        const itemCount = detail?.itemCount ?? 0
        const incrementBy =
          typeof detail?.linesAdded === 'number' && detail.linesAdded > 0
            ? detail.linesAdded
            : itemCount
        const comingFromProductForm =
          detail?.source === 'product-form-component'

        this.renderCartBubble(incrementBy, comingFromProductForm)
      })
      .catch((error) => {
        if (error?.name !== 'AbortError')
          console.warn('[cart-icon] Event promise rejected:', error)
      })
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - Lines added, or absolute cart total when `useAbsoluteCount` path ran in caller.
   * @param {boolean} incrementCount - When true, add `itemCount` to the current bubble value.
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (itemCount, incrementCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0)
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0)
    this.refs.cartBubble.classList.toggle(
      'cart-bubble--animating',
      itemCount > 0 && animate
    )

    this.currentCartCount = incrementCount
      ? this.currentCartCount + itemCount
      : itemCount

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      this.currentCartCount > 0
    )

    if (preferencesProcessingAllowed()) {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(this.currentCartCount),
          timestamp: Date.now()
        })
      )
    }

    if (!animate) return
    await onAnimationEnd(this.refs.cartBubbleText)

    this.refs.cartBubble.classList.remove('cart-bubble--animating')
  }

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!preferencesProcessingAllowed()) return

    const sessionStorageCount = sessionStorage.getItem('cart-count')
    const visibleCount = this.refs.cartBubbleCount.textContent

    if (sessionStorageCount === visibleCount || sessionStorageCount === null)
      return

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount)

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10)

        if (count >= 0) {
          this.renderCartBubble(count, false, false)
        }
      }
    } catch {
      // no-op
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon)
}
